import { create } from "zustand";
import { PLACEHOLDER, SIMULATE } from "utils/constants";
import { Config } from "types/config";
import { SystemStatus, SystemStatusChanges } from "types/system-status";
import { Screens } from "navigation/routes";
import { Alert } from "types/alert";
import { EcoSog } from "types/eco-sog";
import { WeatherData } from "types/weather";
import { WholeBodyVibration } from "types/whole-body-vibration";
import { Captain } from "types/captains";
import { DashboardMode, GuidanceMode, RpmSogMode } from "types/enums";

interface StoreValues {
  screen: Screens;
  // null = value not available from backend, show placeholder
  sog: number | null;
  rog: number | null;
  rpm: number | null;
  impact: number | null;
  redImpacts: number;
  peakImpact: number;
  duration: string;
  distance: string;
  pairingRequest: boolean;
  pairingCode: string;
  guidanceMode: GuidanceMode;
  rpmSogMode: RpmSogMode;
  fuelQuality: number;
  safetyQuality: number | null;
  dashboardMode: DashboardMode;
  showDisclaimer: boolean;
  alertCounter: number;
  config: Config | null;
  systemStatus: SystemStatus | null;
  systemStatusChanges: SystemStatusChanges | null;
  tankCapacity: number;
  tripId: string;
  alert: Alert | null;
  ecoSog: EcoSog | null;
  hasEcoSog: boolean;
  showScrollButtons: boolean;
  showDevTools: boolean;
  weatherData: WeatherData | null;
  wholeBodyVibration: Record<string, WholeBodyVibration>;
  // undefined = not yet received from backend, null = explicitly no captain
  tripCaptain: Captain | null | undefined;
  // Timestamp (ms) of the last message on the "position" topic,
  // null = never received; used to derive GPS connection status
  lastPositionAt: number | null;
  browserSpeed: number | null;
  featureFlags: {
    safetySpeed: boolean;
    ecoSpeed: boolean;
    seaSickness?: boolean;
  };
  safetySpeedEnabled: boolean;
  ecoSpeedEnabled: boolean;
}

type SetterFunction<T> = (value: T) => void;

export interface StoreState extends StoreValues {
  setScreen: SetterFunction<Screens>;
  setSog: SetterFunction<number | null>;
  setRog: SetterFunction<number | null>;
  setRpm: SetterFunction<number | null>;
  setImpact: SetterFunction<number | null>;
  setRedImpacts: SetterFunction<number>;
  setPeakImpact: SetterFunction<number>;
  setDuration: SetterFunction<string>;
  setDistance: SetterFunction<string>;
  setPairingRequest: SetterFunction<boolean>;
  setPairingCode: SetterFunction<string>;
  setGuidanceMode: SetterFunction<GuidanceMode>;
  setRpmSogMode: SetterFunction<RpmSogMode>;
  setFuelQuality: SetterFunction<number>;
  setSafetyQuality: SetterFunction<number | null>;
  setDashboardMode: SetterFunction<DashboardMode>;
  setShowDisclaimer: SetterFunction<boolean>;
  resetAlertCounter: () => void;
  increaseAlertCounter: () => void;
  setConfig: SetterFunction<Config>;
  setSystemStatus: SetterFunction<SystemStatus>;
  setSystemStatusChanges: SetterFunction<SystemStatusChanges>;
  setTankCapacity: SetterFunction<number>;
  setTripId: SetterFunction<string>;
  setAlert: SetterFunction<Alert>;
  setEcoSog: SetterFunction<EcoSog>;
  setHasEcoSog: SetterFunction<boolean>;
  setShowScrollButtons: SetterFunction<boolean>;
  setShowDevTools: SetterFunction<boolean>;
  setWeatherData: SetterFunction<WeatherData | null>;
  setWholeBodyVibration: SetterFunction<WholeBodyVibration>;
  setTripCaptain: SetterFunction<Captain | null>;
  setLastPositionAt: SetterFunction<number | null>;
  setBrowserSpeed: SetterFunction<number | null>;
  setFeatureFlags: SetterFunction<{
    safetySpeed: boolean;
    ecoSpeed: boolean;
    seaSickness?: boolean;
  }>;
  setSafetySpeedEnabled: SetterFunction<boolean>;
  setEcoSpeedEnabled: SetterFunction<boolean>;
}

export const useStore = create<StoreState>()((set) => ({
  screen: Screens.Dashboard,
  showScrollButtons: true,
  // The dev tools panel starts open when simulating
  showDevTools: SIMULATE,

  // dashboard variables
  sog: 0,
  rog: 0,

  rpm: 0,

  impact: 0.0,
  redImpacts: 0,
  peakImpact: 0,
  duration: PLACEHOLDER,
  distance: PLACEHOLDER,

  fuelQuality: 1,
  safetyQuality: 1,
  fuelReduction: 0,
  addedTime: 0,
  tankCapacity: 0,
  alert: null,
  ecoSog: null,
  hasEcoSog: false,
  weatherData: null,
  wholeBodyVibration: {},

  // configuration
  config: null,
  systemStatus: null,
  systemStatusChanges: null,
  showDisclaimer: true,
  pairingRequest: false,
  pairingCode: PLACEHOLDER,
  guidanceMode: GuidanceMode.Safety,
  rpmSogMode: RpmSogMode.Sog,
  dashboardMode: DashboardMode.Gauges,
  alertCounter: 0,
  tripId: "",
  tripCaptain: undefined,
  lastPositionAt: null,
  browserSpeed: null,
  featureFlags: { safetySpeed: true, ecoSpeed: true, seaSickness: true },
  safetySpeedEnabled: true,
  ecoSpeedEnabled: true,

  // set functions
  setScreen: (screen: Screens) => set(() => ({ screen: screen })),
  setSog: (sog: number | null) => set(() => ({ sog: sog })),
  setRog: (rog: number | null) => set(() => ({ rog: rog })),
  setRpm: (rpm: number | null) => set(() => ({ rpm: rpm })),
  setImpact: (impact: number | null) => set(() => ({ impact: impact })),
  setRedImpacts: (redImpacts: number) =>
    set(() => ({ redImpacts: redImpacts })),
  setPeakImpact: (peakImpact: number) =>
    set(() => ({ peakImpact: peakImpact })),
  setDuration: (duration: string) => set(() => ({ duration: duration })),
  setDistance: (distance: string) => set(() => ({ distance: distance })),
  setPairingRequest: (request: boolean) =>
    set(() => ({ pairingRequest: request })),
  setPairingCode: (code: string) => set(() => ({ pairingCode: code })),
  setGuidanceMode: (mode: GuidanceMode) => set(() => ({ guidanceMode: mode })),
  setRpmSogMode: (mode: RpmSogMode) => set(() => ({ rpmSogMode: mode })),
  setFuelQuality: (fuelQuality: number) =>
    set(() => ({ fuelQuality: fuelQuality })),
  setSafetyQuality: (safetyQuality: number | null) =>
    set(() => ({ safetyQuality: safetyQuality })),
  setDashboardMode: (mode: DashboardMode) =>
    set(() => ({ dashboardMode: mode })),
  setShowDisclaimer: (show: boolean) => set(() => ({ showDisclaimer: show })),
  resetAlertCounter: () => set(() => ({ alertCounter: 0 })),
  increaseAlertCounter: () =>
    set((state) => ({ alertCounter: state.alertCounter + 1 })),
  setConfig: (config: Config) => set(() => ({ config: config })),
  setSystemStatus: (systemStatus: SystemStatus) =>
    set(() => ({ systemStatus: systemStatus })),
  setSystemStatusChanges: (systemStatusChanges: SystemStatusChanges) =>
    set(() => ({ systemStatusChanges: systemStatusChanges })),
  setTankCapacity: (tankCapacity: number) =>
    set(() => ({ tankCapacity: tankCapacity })),
  setTripId: (id: string) => set(() => ({ tripId: id })),
  setAlert: (alert: Alert) => set(() => ({ alert: alert })),
  setEcoSog: (ecoSog: EcoSog) => set(() => ({ ecoSog: ecoSog })),
  setHasEcoSog: (hasEcoSog: boolean) => set(() => ({ hasEcoSog: hasEcoSog })),
  setShowScrollButtons: (show: boolean) =>
    set(() => ({ showScrollButtons: show })),
  setShowDevTools: (show: boolean) => set(() => ({ showDevTools: show })),
  setWeatherData: (weatherData: WeatherData | null) =>
    set(() => ({ weatherData: weatherData })),
  setWholeBodyVibration: (data: WholeBodyVibration) =>
    set(() => ({
      wholeBodyVibration: {
        [data.sensor]: data,
      },
    })),
  setTripCaptain: (captain: Captain | null) =>
    set((state) =>
      // Only skip the update when the same captain is already set;
      // null/undefined must always update so "no captain" becomes known
      state.tripCaptain && captain && state.tripCaptain.id === captain.id
        ? state
        : { tripCaptain: captain }
    ),
  setLastPositionAt: (lastPositionAt: number | null) =>
    set(() => ({ lastPositionAt: lastPositionAt })),
  setBrowserSpeed: (browserSpeed: number | null) =>
    set(() => ({ browserSpeed: browserSpeed })),
  setFeatureFlags: (featureFlags) => set(() => ({ featureFlags })),
  setSafetySpeedEnabled: (enabled) =>
    set(() => ({ safetySpeedEnabled: enabled })),
  setEcoSpeedEnabled: (enabled) => set(() => ({ ecoSpeedEnabled: enabled })),
}));
